// Shared helpers for the order form (NewOrder + OrderPage's edit mode).
// Lifted out of both pages so the two stay aligned by construction.
import { toPayloadAmount, toPayloadNumber, toPayloadQuantity, isMalformedAmount } from './moneyPayload';

/**
 * ⚠️ LEGACY VOCABULARY. `components/OrderForm.js` stopped importing this on
 * 2026-08-09 when the urgency picker was removed — nothing in the app now
 * OFFERS these choices, and no surface writes `orders.urgency`.
 *
 * 🔑 It is kept, not deleted, because 1,736 orders carry one of these values
 * and something has to be able to LABEL them. Deleting the list would leave
 * every legacy order rendering a raw enum like `2_3_days`. Its live readers are
 * the label maps in `constants/statuses.js` and `constants/colors.js`.
 *
 * **Do not re-import this into a form.** A control built from it would start
 * writing the column again, which is the thing increment 3 stopped.
 */
export const urgencyOptions = [
  { value: 'same_day', label: 'Same Day', color: 'bg-red-100 border-red-400 text-red-800 hover:bg-red-200' },
  { value: 'next_day', label: 'Next Day', color: 'bg-orange-100 border-orange-400 text-orange-800 hover:bg-orange-200' },
  { value: '2_3_days', label: 'Within 2-3 Days', color: 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200' },
  { value: '7_days', label: 'Within 7 Days', color: 'bg-green-100 border-green-400 text-green-800 hover:bg-green-200' },
  { value: 'custom', label: 'Custom Date', color: 'bg-blue-100 border-blue-400 text-blue-800 hover:bg-blue-200' },
];

// A NEW line's own name for itself (2026-09-14) — `client_key` travels in the
// POST/PUT body and the server echoes it on the row the line was INSERTED as
// (`server/helpers/orderItemClientKey.js`), so a file staged against the line
// before it had an id lands on THAT line's new id, never on a position. ≤ 64.
let clientKeySeq = 0;
// THE item categories: what the picker offers, in order, and what a stored
// `order_items.item_category` READS as. The stored value is the key (the
// hire-mode rules and the server read it; the column is free text), the label
// is only wording. Owner 2026-10-11: "Asset" reads "Potential Asset" (his
// fallback: "Potential future asset" is 140px and the picker has 125px at 1280
// wide, measured), "Service" reads "Services", "Parts & Materials" is new and
// "Tool" is no longer offered. Every surface that shows a category (the
// picker, the read-only value, its tooltip, the order PDF) goes through
// `itemCategoryLabel`, so the wording cannot differ between them.
export const ITEM_CATEGORY_OPTIONS = ['Consumable', 'Parts & Materials', 'Asset', 'Hire', 'Service', 'Other'];
export const ITEM_CATEGORY_LABELS = { Asset: 'Potential Asset', Service: 'Services' };
export function itemCategoryLabel(category) {
  return ITEM_CATEGORY_LABELS[category] || category || '';
}
// A line saved with a category no longer offered ("Tool") keeps it: a
// <select> whose value has no option renders BLANK, which would read as
// "no category" and lose it on the next save. So the line's own retired
// value is appended for that line only; it cannot be picked anywhere else.
export function itemCategoryOptions(current) {
  return current && !ITEM_CATEGORY_OPTIONS.includes(current)
    ? [...ITEM_CATEGORY_OPTIONS, current]
    : ITEM_CATEGORY_OPTIONS;
}

export function newItemClientKey() {
  clientKeySeq += 1;
  return `ck-${Date.now().toString(36)}-${clientKeySeq}`;
}

// Empty line item template — id is omitted so the server treats it as new.
export function createEmptyItem() {
  return {
    client_key: newItemClientKey(),
    part_number: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    needed_by_date: '',
    // 🟡 A NEW item still defaults to 'Unknown', and that is a DELIBERATE
    // non-change (2026-08-09) — not the defect fixed in `buildInitialItems`.
    //
    // The two are different acts. Hydration substituted a value for a stored
    // NULL on an item the user never looked at, so a save of an unrelated field
    // wrote a 3-day lead time nobody entered and nothing said so. Here the user
    // is ADDING a line.
    //
    // 🚨 **THE JUSTIFICATION THIS COMMENT SHIPPED WITH DIED AT THE 2026-08-09
    // INTEGRATION MERGE, AND THE VALUE OUTLIVED IT. FLAG FOR THE OWNER.**
    // It read: *"the `<select>` shows `Unknown` on screen before they submit,
    // and they can change it — including to the `Not stated` option that now
    // exists. Nothing is invented behind anyone's back."* Both halves were true
    // of `fix/review-blockers-2026-08-09` and are FALSE of the merged tree:
    // `feat/lead-time-increment-3` DELETED the per-item lead-time control in the
    // same batch, so there is no `<select>`, no `Not stated` option, and no
    // read-only display either. **`'Unknown'` is now invented behind the
    // author's back on the CREATE path** — invisible before submit, invisible
    // after, and `LEAD_TIME_PRESETS` (`server/helpers/leadTimeHelpers.js:27`)
    // maps it to **3 days**. That is precisely the shape the edit-path fix
    // above exists to stop; it simply moved doors.
    //
    // ✅ **THE OWNER RULED ON 2026-08-09 AND THE FLAG IS CLOSED — option (a) of
    // the two this comment used to offer. THE VALUE HERE IS STILL `'Unknown'`,
    // AND THAT IS NOW A CONSEQUENCE OF THE RULING RATHER THAN AN OPEN QUESTION.**
    // He set the ORDER-LEVEL default: *"default lead time should be set to 3
    // days as its ther orderes responsability to state if its longer. I dont
    // want to nag the buyer"*. `buildInitialForm` (`components/OrderForm.js`)
    // now opens every NEW order on `DEFAULT_NEW_ORDER_LEAD_TIME_DAYS` = 3, in
    // the visible `<select>`, and `computeSlackDetail` resolves the ORDER-LEVEL
    // lead time FIRST — so on a new order this string is never consulted at all.
    // The invisible-default defect is closed at the level that was actually
    // driving the maths.
    //
    // 🟢 **AND THE TWO AGREE ON THE NUMBER, WHICH IS WHY NOTHING MOVED.**
    // `LEAD_TIME_PRESETS` (`server/helpers/leadTimeHelpers.js`) maps `'unknown'`
    // to **3 days**, the same 3 the order-level default now states out loud. So
    // a new order's slack, dot, `required_by_date`, sort position and reported
    // `leadDays` are byte-identical before and after the ruling. Pinned by
    // `server/tests/regressions/order-lead-time-default-equivalence.test.js`.
    //
    // ⚠️ **STILL DO NOT FLIP THIS TO `''` AS A TIDY-UP — the reason has changed
    // and is now sharper.** `'Unknown'` is TRUTHY, which is what makes
    // `computeSlackDetail` report the resolved lead time as *stated* rather than
    // assumed (`item.lead_time ? leadDays : null`). Flipping it to `''` would
    // drop every such item onto the silent `: 1` default — changing the slack of
    // an order created between this ruling and that change by two days, and
    // turning a reported "3 day lead time" into no clause at all. It would also
    // break the equivalence the test above pins. If the per-item column is ever
    // retired, retire it as its own change with the movement measured.
    lead_time: 'Unknown',
    is_hire: false,
    hire_from: '',
    hire_to: '',
    hire_rate_type: 'daily',
    hire_rate: 0,
    daily_rate: 0,
    weekly_rate: 0,
    total_hire_rate: 0,
    item_notes: '',
    item_category: '',
    cost_code_id: '',
    payment_type_id: '',
  };
}

// Normalise a single line item's hire-rate fields so daily/weekly/total stay
// in sync with the selected rate_type before submission.
//
// 🚨 THIS IS A PAYLOAD BUILDER — its output goes straight into the POST/PUT
// body, so what it cannot read must become `null` for the server to refuse,
// never a plausible number. It used to read all three fields as
// `parseFloat(x) || 0`, and `parseFloat('1,000')` is `1`: a thousandfold
// under-price stored with a 2xx and no signal anywhere. See
// `utils/moneyPayload.js` — in particular why `Number.isFinite(parseFloat(x))`
// is NOT the house rule and `Number.isFinite(Number(x))` is.
//
// ⚖️ A REAL TYPED `0` STILL SENDS as `0` on `unit_price` and `hire_rate`; only
// a BLANK field defaults. 🚨 `quantity` is the deliberate exception — a `0`
// there means ONE (root CLAUDE.md's `hireCost.js` row) and `toPayloadQuantity`
// keeps that ruling intact while still refusing malformed input.
export function normalizeItemRate(item) {
  const rate = toPayloadAmount(item.hire_rate, 0);
  return {
    ...item,
    id: item.id || undefined,
    quantity: toPayloadQuantity(item.quantity),
    unit_price: toPayloadAmount(item.unit_price, 0),
    daily_rate: item.hire_rate_type === 'daily' ? rate : 0,
    weekly_rate: item.hire_rate_type === 'weekly' ? rate : 0,
    total_hire_rate: rate,
    hire_rate_type: item.hire_rate_type || 'daily',
  };
}

// Build the API payload for POST /api/orders or PUT /api/orders/:id.
// `extras` lets the caller merge any context-specific keys (e.g. department_id
// on create) without duplicating the rest of the assembly.
//
// 🚨 `production_id` IS DELIBERATELY DROPPED HERE, AND THE DESTRUCTURE BELOW IS
// THE MECHANISM — deleting the old `production_id:` line alone would NOT have
// worked. This function opens by SPREADING `form`, so the field would have gone
// on being sent regardless: as the form's STRING rather than the int the old
// explicit line produced. That is a silent type change, invisible in a diff, on
// a field the server currently ignores — harmless right up until somebody makes
// the server read it again.
//
// 🔑 WHY IT IS DROPPED AT ALL (2026-08-12). `orders.production_id` is now
// DERIVED from the order's own department on create (`routes/orders.js`,
// `finalProductionId` ← `SELECT production_id FROM departments WHERE id = ?`)
// and PRESERVED from the stored row on update. The body value is not merely
// ignored, it is UNBINDABLE — `production_id` is no longer destructured from
// `req.body` in that file at all, on either verb. Continuing to send it would
// just move the lie from the UI into the wire.
//
// ⚠️ `form.production_id` STILL EXISTS and is still hydrated on the edit path.
// It is a DISPLAY value now — it is what the read-only Production field states
// about an existing order — so do not "finish the job" by deleting it from
// `EMPTY_FORM` / `buildInitialForm`. Pinned by §4 of
// `components/OrderForm.productionReadOnly.test.js`.
export function buildPayload(form, items, extras = {}) {
  const { production_id: _productionIdIsServerDerived, ...formWithoutProduction } = form;
  return {
    ...formWithoutProduction,
    only_use_this_supplier: !!form.only_use_this_supplier,
    // LEGACY, ROUND-TRIPPED, NEVER AUTHORED (2026-08-09). No control writes
    // `form.urgency` any more — the picker is gone and urgency is derived from
    // slack behind the scenes — but `buildInitialForm` still hydrates an
    // existing value, so editing a legacy order sends its urgency back
    // unchanged and the server keeps reading it as the deadline fallback.
    // `EMPTY_FORM.urgency` is `null`, so a NEW order stores nothing.
    // 🚨 Do NOT "tidy" this line away as dead: deleting it makes every save of
    // a legacy order drop `urgency` from the payload, and `PUT /api/orders/:id`
    // would write the column NULL — silently blanking the only deadline 988 of
    // the 1,736 orders in the dev database have.
    urgency: form.urgency || null,
    // 🚨 **THE TRAP THIS FEATURE WAS MOST LIKELY TO SHIP.** This read
    // `form.urgency === 'custom' ? form.custom_needed_by_date : null` until
    // 2026-08-09 — one of SEVEN `urgency === 'custom'` guards in production
    // code, and the only one that WRITES. With the urgency picker deleted,
    // `form.urgency` is null on every new order and on every legacy order that
    // was not 'custom', so this expression would have sent `null` on EVERY
    // submit: the user fills in the newly always-visible Needed By Date, presses
    // Save, gets a 200, and the date is gone. No error, no validation failure,
    // nothing on screen. Pinned by `orderFormHelpers.buildPayload.test.js`.
    // `|| null` is correct here and is NOT the falsy trap that governs
    // `lead_time_days` below: this is a DATE STRING, `''` is the empty control
    // and is not a legitimate date, so there is no valid falsy value to lose.
    custom_needed_by_date: form.custom_needed_by_date || null,
    // ORDER-LEVEL lead time. Form state holds the `<select>`'s STRING; the API
    // takes an integer number of days, or `null` for "not stated".
    //
    // 🚨 **`=== ''` AND NOT A TRUTHINESS TEST.** `'0'` is "Same day" — a real
    // option on the scale — and the idiomatic `form.lead_time_days || null`
    // would map it to `null`, so the one lead time that most needs recording
    // would be the one silently discarded on submit. This is the same shape as
    // the bug that made `leadTimeToDays` return `0` for a parse failure, and
    // the same shape as the `!value` trap in the order-list slack sort: a falsy
    // value that is also a legitimate answer can never double as the absence
    // marker. `parseInt` is safe here only BECAUSE the empty case is already
    // handled — `parseInt('', 10)` is `NaN`, which JSON-serialises to `null`
    // and would have hidden the fault.
    lead_time_days: form.lead_time_days === '' || form.lead_time_days === null || form.lead_time_days === undefined
      ? null
      : parseInt(form.lead_time_days, 10),
    // MY SPACE "HEADER DETAILS" (2026-09-11) — trimmed, blank → null.
    //
    // 🪤 THE KEY IS ALWAYS PRESENT, AND `null` IS NOT THE SAME AS OMITTING IT.
    // The server distinguishes the two deliberately: an ABSENT key means the
    // caller said nothing, so the stored value and the user's default both
    // survive; `null` means the user CLEARED the box, which the owner ruled
    // clears the order AND the default ("blank means blank"). A `buildPayload`
    // that dropped the key on blank would make clearing impossible while
    // looking perfectly correct on screen — the same silent shape as the
    // `po_number !== undefined` rule on the server.
    //
    // 🟢 `|| null` after the trim is correct here and is NOT the
    // `lead_time_days` falsy trap: this is free text, `''` is the empty control
    // and is not a legitimate value, so there is no falsy answer to lose.
    // 🪤 The trim is on the ENDS only — internal line breaks are the entire
    // point of the field and must survive.
    header_details: (typeof form.header_details === 'string' && form.header_details.trim())
      ? form.header_details.trim()
      : null,
    items: items
      .filter(item => item.description.trim())
      .map(normalizeItemRate),
    ...extras,
  };
}

// Validate the form. Returns an errors object — empty when valid.
//
// 🔻 THE PRODUCTION CHECK IS GONE (2026-08-12), WITH THE `hasNoAssignments`
// OPTION THAT EXISTED ONLY TO EXEMPT ONE COHORT FROM IT. It read
// `if (!form.production_id && !hasNoAssignments)`. Production is no longer a
// control — it is a read-only statement of which production the order belongs
// to, derived server-side from the department — so there is nothing here a user
// can get wrong and nothing this check could protect.
//
// 🚨 REMOVING IT IS REQUIRED, NOT TIDYING. The field stops populating
// `form.production_id` on the create path, so leaving the check would block
// submit for **every user WITH assignments** — `hasNoAssignments` is false for
// exactly them. Note the direction: the cohort that reads as at-risk (a user
// with NO assignments) was always exempt and is the one cohort this could never
// have blocked. `server/routes/orders.js` carried that backwards until
// 2026-08-12, which invites a reader to check the named cohort, find it handled,
// and conclude the whole warning is obsolete.
//
// ⚠️ `hasNoAssignments` still EXISTS in `components/OrderForm.js` — it gates the
// Sub Department select's `Not Assigned` option. Only this consumer went.
// Pinned by §5 of `components/OrderForm.productionReadOnly.test.js`, whose §5.2
// is the not-vacuous half: the other required fields must still block.
//
// 📅 `storedItems` (2026-09-28) — the order's SAVED rows (`initialOrder.items`;
// `[]` on create). Read ONLY by the hire-date checks below, which mirror
// `server/routes/orders.js#findHireDateViolation` and need the stored row for
// the same reason: a legacy line saved with no date must not block an
// unrelated edit. Owner rulings 2026-09-28: "a hire line must have an end date
// to save. People put their best estimate and use Extend when it runs on",
// then "Yes, require both dates".
export const HIRE_END_DATE_REQUIRED_MESSAGE =
  'Last day of hire is required. Put your best estimate; you can extend it later.';
export const HIRE_START_DATE_REQUIRED_MESSAGE = 'First day of hire is required.';
/** An order line's Quantity holds text that is not a number (input audit #7). */
export const QUANTITY_NOT_A_NUMBER = 'Quantity is not a number — type a number such as 2';

const isBlankDate = (v) => v == null || (typeof v === 'string' && v.trim() === '');

// Same three arms as the server, per field: a NEW hire line, a line flipping
// to hire, or a line whose STORED date is being cleared. Stored NULL + blank
// passes.
function needsHireDate(item, storedById, field) {
  if (!item.is_hire || !isBlankDate(item[field])) return false;
  const stored = item.id != null ? storedById.get(item.id) : undefined;
  if (!stored) return true;
  if (!stored.is_hire) return true;
  return !isBlankDate(stored[field]);
}

export function validateForm(form, items, storedItems = []) {
  const errors = {};
  const storedById = new Map((storedItems || []).map(s => [s.id, s]));

  if (!form.requested_by.trim()) {
    errors.requested_by = 'Requested By is required';
  }
  if (form.buyer_email && form.buyer_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.buyer_email.trim())) {
    errors.buyer_email = 'Please enter a valid email address';
  }

  const hasValidItem = items.some(item => item.description.trim());
  if (!hasValidItem) {
    errors.items = 'At least one item with a description is required';
  }

  const itemErrors = {};
  items.forEach((item, index) => {
    // 🚨 A SAVED LINE WITH A BLANK DESCRIPTION IS AN ERROR, NEVER A DROP
    // (owner ruling, 2026-09-23: "block it with a message"). `buildPayload`
    // filters blank-description rows out of the PUT, and `PUT /api/orders/:id`
    // DELETES every existing item the payload omits — so clearing a saved
    // line's description and pressing Save silently deleted a priced line
    // (measured: a £750 line gone at 200, visible only in the audit log).
    // Deleting a line is the deliberate Remove flow's job, never Save's.
    // 🔑 Only rows with an `id` — a NEW blank row (no `id`) is still dropped
    // exactly as before: it has nothing on the server to lose.
    // 🪤 No "Item N" here: the error summary prefixes every item message with
    // it. And Remove is not offered on the LAST line, so the advice is dropped
    // there rather than pointing at a control that does not exist.
    if (item.id != null && !item.description.trim()) {
      itemErrors[index] = {
        description: items.length > 1
          ? 'Description is required, or use Remove to delete this item'
          : 'Description is required',
      };
      return;
    }
    if (item.description.trim()) {
      const rowErrors = {};
      // 🪤 `toPayloadAmount(…, null)`, NEVER `parseFloat`. `parseFloat('1,000')`
      // is `1` — finite, positive — so this check PASSED the exact input the
      // server now refuses, and `normalizeItemRate` then sent `1`. A guard can
      // look correct, read correctly in review and still be wrong about one
      // input class. Blank still fails here exactly as it always did
      // (blank → `null` → refused on the next line), so nothing loosened.
      // 🚨 `toPayloadNumber`, not `toPayloadAmount` (2026-09-22): the VALIDATOR
      // must ask the same question the PAYLOAD builder answers, and
      // `normalizeItemRate` sends `toPayloadQuantity`, which does not round.
      // A validator that rounds where the builder does not is a guard testing a
      // value the server will never see.
      const qty = toPayloadNumber(item.quantity, null);
      // Input audit #7 (2026-10-29): UNREADABLE text (`ui/NumberInput`'s marker, `1,000`, `two`) is
      // told it is not a number — "must be a positive number" was the right refusal for the wrong
      // reason. Blank, 0 and negatives keep the old message; fractions stay ALLOWED (no ruling).
      if (isMalformedAmount(item.quantity)) {
        rowErrors.quantity = QUANTITY_NOT_A_NUMBER;
      } else if (qty === null || qty <= 0) {
        rowErrors.quantity = 'Quantity must be a positive number';
      }
      // 🔑 The two MONEY fields had no client check at all — a malformed amount
      // was silently converted to `0` and stored with a 2xx. Refused HERE, with
      // the form left exactly as the user left it, rather than as a bare 400.
      // ⚖️ BLANK IS LEGITIMATE and is not refused: blank means `0` on these
      // columns, and a real typed `0` is a real price. Only an UNREADABLE value
      // is an error — `isMalformedAmount` answers false for both.
      // 🪤 These keys reach the user through `OrderForm.js`'s error summary,
      // which iterates `Object.values(errs)` and so needs no change; only
      // `quantity` additionally renders inline under its own control.
      if (isMalformedAmount(item.unit_price)) {
        rowErrors.unit_price = 'Unit price must be a number — for example 1000, not 1,000';
      }
      if (isMalformedAmount(item.hire_rate)) {
        rowErrors.hire_rate = 'Hire rate must be a number — for example 1000, not 1,000';
      }
      if (needsHireDate(item, storedById, 'hire_from')) {
        rowErrors.hire_from = HIRE_START_DATE_REQUIRED_MESSAGE;
      }
      if (needsHireDate(item, storedById, 'hire_to')) {
        rowErrors.hire_to = HIRE_END_DATE_REQUIRED_MESSAGE;
      }
      if (Object.keys(rowErrors).length > 0) {
        itemErrors[index] = rowErrors;
      }
    }
  });
  if (Object.keys(itemErrors).length > 0) {
    errors.itemErrors = itemErrors;
  }

  return errors;
}
